import React, { FC, useEffect, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowUpRightFromSquare } from '@fortawesome/free-solid-svg-icons';
import { useLocation } from 'react-router-dom';
import { toAbsoluteUrl } from '../../utils';
import { LottieAnimation } from '../LottieAnimation';
import { ButtonComponent, BUTTON_TYPE } from '../ButtonComponent';
import { BASE_URL } from '../../../config/environmentConfig';

import './LoginLayout.scss';

interface ISequenceProps {
  key: string;
  path: string;
}

// Provider names configuration
const PROVIDER_NAMES: ISequenceProps[] = [
  {
    key: 'Braintree',
    path: '/media/partners/Braintree.png',
  },
  {
    key: 'Bridgerpay',
    path: '/media/partners/Bridgerpay.png',
  },
  {
    key: 'CheckoutChamp',
    path: '/media/partners/CheckoutChamp.png',
  },
  {
    key: 'GlobalPayments',
    path: '/media/partners/GlobalPayments.png',
  },
  {
    key: 'OpenPath',
    path: '/media/partners/OpenPath.png',
  },
  {
    key: 'Spreedly',
    path: '/media/partners/Spreedly.png',
  },
  {
    key: 'Sticky',
    path: '/media/partners/Sticky.png',
  },
  {
    key: 'Stripe',
    path: '/media/partners/Stripe.png',
  },
  {
    key: 'TSYS',
    path: '/media/partners/TSYS.png',
  },
  {
    key: 'ValorPaytech',
    path: '/media/partners/ValorPaytech.png',
  },
  {
    key: 'Worldpay',
    path: '/media/partners/Worldpay.png',
  },
];

// New approach: All 4 blocks cycle through all 11 logos together
const TOTAL_BLOCKS = 4;
const TOTAL_LOGOS = PROVIDER_NAMES.length; // 11 logos

// Get the current logo for a specific block based on the global iteration
const getCurrentLogo = (blockIndex: number, iteration: number): ISequenceProps => {
  const logoIndex = (iteration * TOTAL_BLOCKS + blockIndex) % TOTAL_LOGOS;
  return PROVIDER_NAMES[logoIndex];
};

// Create sequence for upward scrolling animation
const createLogoSequence = (blockIndex: number, currentIteration: number): ISequenceProps[] => {
  const sequence = [];

  // Create sequence for smooth upward scroll
  // Current logo, next logo, and one more for seamless transition
  for (let i = 0; i < 3; i++) {
    const iteration = currentIteration + i;
    sequence.push(getCurrentLogo(blockIndex, iteration));
  }

  return sequence;
};

interface Props {
  children: React.ReactElement;
}

export const LoginLayout: FC<Props> = ({ children }) => {
  const location = useLocation();
  const [bg, setBg] = useState<any>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [animationKey, setAnimationKey] = useState(0);
  const [currentIteration, setCurrentIteration] = useState(0);
  const [animationState, setAnimationState] = useState(0); // 0, 1, 2 for upward scroll
  const [isResetting, setIsResetting] = useState(false);

  const getBG = async (): Promise<void> => {
    try {
      setIsLoaded(false);
      const response = await fetch('/media/background.json');
      const data = await response.json();
      setBg(data);

      // Longer delay for more elegant fade-in
      setTimeout(() => {
        setIsLoaded(true);
      }, 300);
    } catch (error) {
      console.error('Failed to load background animation:', error);
      setIsLoaded(true); // Show layout even if animation fails
    }
  };

  // Reset animation when route changes to prevent sticking
  useEffect(() => {
    setAnimationKey(prev => prev + 1);
    void getBG();
  }, [location.pathname]);

  // Partner logo animation logic with global iteration cycling
  useEffect(() => {
    const logoInterval = setInterval(() => {
      setAnimationState(prev => {
        const nextState = prev + 1;

        // After 3 states (0, 1, 2), move to next iteration
        if (nextState >= 3) {
          // Move to next iteration (next set of logos)
          setCurrentIteration(prevIteration => prevIteration + 1);

          // Reset animation state with seamless transition
          setIsResetting(true);
          setTimeout(() => {
            setAnimationState(1); // Reset to state 1 for seamless loop
            setIsResetting(false);
          }, 800);

          return 2; // Stay at state 2 for this cycle
        }

        return nextState;
      });
    }, 4000); // 4s pause + 2s transition

    return () => clearInterval(logoInterval);
  }, []);

	return (
    <div className="login-layout-container">
      {/* Left Column */}
      <div className="login-layout-left-column d-none d-xl-flex position-relative">
        <div className="lottie-container">
          {bg && (
            <LottieAnimation
              key={animationKey} // Force re-render on route change
              animationData={bg}
              speed={0.25}
              loop={true}
              autoplay={true}
              width="100%"
              height="100%"
              className={`lottie-animation-full-cover ${isLoaded ? 'fade-in' : 'fade-out'}`}
            />
          )}
          {!isLoaded && (
            <div className="lottie-loading-overlay">
              <div className="loading-gradient"></div>
            </div>
          )}
        </div>

        <div className="position-absolute login-layout-logo">
          <img
            className="flex-logo-letter"
            src={toAbsoluteUrl('/media/flex-logo-letter-white.png')}
            alt="Flex Logo"
          />
        </div>

        <div className="position-absolute login-layout-website-btn">
          <ButtonComponent
            buttonType={BUTTON_TYPE.WHITE}
            additionalClass="open-website-btn no-letter-spacing"
            onButtonClick={(): Window => window.open(BASE_URL.LOGIN_URL, '_blank')}
          >
            <>
              Open website
              <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="ms-2" />
            </>
          </ButtonComponent>
        </div>

        <div className="position-absolute login-layout-footer w-100">
          {/*<div className="login-layout-headline fw-bolder text-center mx-auto mb-5">*/}
          {/*  <div className="headline-text">*/}
          {/*    Turn payment declines<br />*/}
          {/*    into increased revenues*/}
          {/*  </div>*/}
          {/*</div>*/}

          {/* Partner Logo Animation Section */}
          <div className="login-layout-partners">
            <div className="partners-title mb-5 fs-5 fw-bolder">Our partners</div>
            <div className="partners-logo-wrapper">
              <div className="partners-logo-container">
                {
                  Array.from({ length: 4 }, (_, blockIndex) => {
                    const logoSequence = createLogoSequence(blockIndex, currentIteration);
                    const blockClass = isResetting
                      ? `logo-block logo-state-reset`
                      : `logo-block logo-state-${animationState}`;

                    return (
                      <div key={blockIndex} className={blockClass}>
                        <div className="logo-content" style={{ transitionDelay: `${blockIndex * 300}ms` }}>
                          {
                            logoSequence.map((partnerLogo, logoIndex) => (
                              <div key={`${partnerLogo.key}-${logoIndex}-${currentIteration}`} className="logo-placeholder">
                                <img
                                  className="w-100"
                                  src={toAbsoluteUrl(partnerLogo.path)}
                                  alt={partnerLogo.key}
                                />
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    );
                  })
                }
              </div>
            </div>
          </div>
        </div>

      </div>

      {/* Right Column */}

      {children}

    </div>
  );
};
